import { STARTING_LEVEL, type SkillLevels } from './maths-skills.js'
import { type MosaicSize } from './mosaic-solve.js'

/**
 * What she is getting better at, in Maritza's Mosaic.
 *
 * **A skill is a grid size**, exactly as in Stripey's Sudoku and for the same
 * reason: a mosaic is one picture for ten minutes, and what she meets in it is
 * whatever deductions that picture happens to want, in an order nobody chose.
 * The size is what she picked, what she spent the round on, and the thing that
 * gets easier with practice.
 *
 * Not a picture, either — tempting, because the pictures are hand-drawn and
 * countable, but recording `hoppity-whole` would say she is good at Hoppity
 * rather than good at fifteen across, and the set is small enough that she
 * would exhaust each one long before the level meant anything.
 */
export interface MosaicSkill {
  readonly key: string
  /** Shown to a parent, not to the player. */
  readonly label: string
  readonly size: MosaicSize
}

export const MOSAIC_SKILLS: readonly MosaicSkill[] = [
  { key: 'mosaic.5x5', label: 'Five-across pictures', size: 5 },
  { key: 'mosaic.10x10', label: 'Ten-across pictures', size: 10 },
  { key: 'mosaic.15x15', label: 'Fifteen-across pictures', size: 15 },
]

export function mosaicSkillOf(size: MosaicSize): MosaicSkill {
  const skill = MOSAIC_SKILLS.find((candidate) => candidate.size === size)
  // The list above is exhaustive over MosaicSize by construction and a test
  // holds that, so reaching here is a missing entry rather than a bad input.
  if (!skill) throw new Error(`No skill for a ${size}×${size} picture`)

  return skill
}

/** Where she stands at this size, or the starting level if she has never played it. */
export function mosaicLevelFor(levels: SkillLevels, size: MosaicSize): number {
  return levels[mosaicSkillOf(size).key] ?? STARTING_LEVEL
}
